/**
 * planQuotas.ts — Story 6.3 (Pricing-Tabelle): Doppel-Kontingente + Laufzeitrabatte.
 *
 * QUELLE DER KONTINGENT-CHIPS: GET /api/v2/subscription/plans liefert die in
 * Story 6.1/6.2 kalibrierten Doppel-Limits als `mobile_hotspot_limit` /
 * `iot_hotspot_limit` (Api::V2::PlanSerializer, gespeist aus denselben
 * Konstanten wie der HotspotQuotaEnforcer:
 * Authentication::SubscriptionService::MOBILE_HOTSPOT_LIMITS/IOT_HOTSPOT_LIMITS).
 * Diese Felder werden BEVORZUGT gelesen — eine Rekalibrierung im Backend
 * schlaegt damit unmittelbar auf die Preisliste durch (Story 6.2 AC2).
 *
 * PLAN_QUOTA_FALLBACK greift nur noch, wenn eine Antwort GAR KEINE
 * Kontingent-Felder traegt (aeltere Deploy-Stufe). Er ist KEINE Erfindung,
 * sondern die per Entscheidungslog (Story 6.2, PO-Kalibrierung Dennis
 * 18.08.2026) festgelegte Matrix, und kann entfallen, sobald keine alte
 * Backend-Version mehr im Feld ist.
 */
import type { Plan } from '../subscription/useSubscriptionPlans'

/**
 * Kontingent einer Hotspot-Art:
 * - Zahl        → konkretes Limit
 * - 'unlimited' → Backend meldet ausdruecklich KEIN Limit (Feld = null)
 * - 'unknown'   → weder Contract-Feld noch Fallback-Matrix kennen den Plan
 *
 * Welle-4b-Review F3: 'unknown' MUSS als „unbekannt“ gerendert werden — ein
 * unbekannter Plan als „Unbegrenzt“ zu verkaufen waere eine Falschaussage.
 */
export type PlanQuota = number | 'unlimited' | 'unknown'

export type PlanQuotas = {
  mobile: PlanQuota
  iot: PlanQuota
}

/**
 * Entscheidungslog Story 6.2 — finale Kalibrierung (identisch zu
 * Authentication::SubscriptionService im Backend; 'free' aus dem
 * Entscheidungslog, dort Fallback-Pfad statt Matrix).
 */
export const PLAN_QUOTA_FALLBACK: Record<string, PlanQuotas> = {
  free:         { mobile: 1,  iot: 3 },
  single:       { mobile: 1,  iot: 5 },
  starter:      { mobile: 1,  iot: 15 },
  ideal:        { mobile: 2,  iot: 30 },
  professional: { mobile: 5,  iot: 75 },
  business:     { mobile: 15, iot: 250 },
  enterprise:   { mobile: 30, iot: 500 },
  enterprisex:  { mobile: 40, iot: 750 },
  enterprisexl: { mobile: 50, iot: 1000 },
}

/** Einzelfeld aus dem Contract deuten: null = unbegrenzt, fehlend = unbekannt. */
function apiQuota(value: number | null | undefined): PlanQuota {
  if (value === undefined) return 'unknown'
  if (value === null) return 'unlimited'
  return value
}

type QuotaFields = Pick<
  Plan,
  'id' | 'mobile_hotspot_limit' | 'iot_hotspot_limit' | 'max_mobile_hotspots' | 'max_iot_hotspots'
>

/**
 * Doppel-Kontingent eines Plans: API-Felder bevorzugt, sonst Fallback-Matrix.
 *
 * Feldnamen: der PlanSerializer liefert `mobile_hotspot_limit`/
 * `iot_hotspot_limit`; `max_mobile_hotspots`/`max_iot_hotspots` werden als
 * Alias weiterhin akzeptiert. Vorher las das FE ausschliesslich die
 * `max_*`-Namen — die nie ankamen — und rendert die Chips daher dauerhaft aus
 * der hartkodierten Fallback-Matrix (Story 6.2 AC2 unerfuellt: eine
 * BE-Rekalibrierung waere in der Preisliste unsichtbar geblieben).
 */
export function planQuotas(plan: QuotaFields): PlanQuotas {
  const mobile = plan.mobile_hotspot_limit !== undefined
    ? plan.mobile_hotspot_limit
    : plan.max_mobile_hotspots
  const iot = plan.iot_hotspot_limit !== undefined
    ? plan.iot_hotspot_limit
    : plan.max_iot_hotspots

  if (mobile !== undefined || iot !== undefined) {
    return { mobile: apiQuota(mobile), iot: apiQuota(iot) }
  }
  // F3: Unbekannte plan.id -> 'unknown'. Frueher lieferte dieser Pfad
  // { mobile: null, iot: null } und die Chips behaupteten „Unbegrenzt“.
  return PLAN_QUOTA_FALLBACK[plan.id] ?? { mobile: 'unknown', iot: 'unknown' }
}

/**
 * Ergebnis von planMonthlyPrice — Betrag PLUS Herkunft.
 *
 * `fromBackend` entscheidet, ob die UI ueberhaupt einen Laufzeit-Rabatt
 * behaupten darf (Streichpreis/Prozentangabe). Siehe Kommentar unten.
 */
export type PlanMonthlyPrice = {
  /** Monatspreis in der Anzeige-Waehrung des Plans. */
  amount: number
  /** true = aus durations[].price des Backends berechnet (verbindlich). */
  fromBackend: boolean
}

/**
 * Effektiver MONATSpreis eines Plans bei gewaehlter Laufzeit.
 *
 * EHRLICHKEITS-REGEL (Welle-4b-Review F1): Einzige Quelle fuer einen
 * rabattierten Preis ist die vom Backend gelieferte `durations`-Matrix
 * (GET /api/v2/subscription/plans → durations[].price = GESAMTpreis der
 * Laufzeit in Anzeige-Waehrung). Frueher rechnete das FE bei fehlender Matrix
 * selbst „Basispreis × (1 − 10/20/30 %)“ — Stripe kassierte aber den vollen
 * Betrag, weil der Contract das Feld gar nicht kennt. Deshalb: ohne
 * Backend-Preis wird der BASIS-Monatspreis gezeigt und `fromBackend: false`
 * gemeldet; die UI unterdrueckt dann jede Rabatt-/Prozentaussage und weist auf
 * den Endpreis im Stripe-Checkout hin.
 *
 * BE-FOLLOW-UP: Sobald `durations[].price` (und `stripe_price_id` je Laufzeit)
 * im OpenAPI-Plan-Schema deklariert und vom PlanSerializer geliefert wird,
 * greift automatisch der rabattierte Pfad — im FE ist dafuer keine Aenderung
 * noetig.
 */
export function planMonthlyPrice(plan: Plan, months: number): PlanMonthlyPrice {
  const entry = plan.durations?.find((d) => d.months === months)
  if (entry && typeof entry.price === 'number' && months > 0) {
    return { amount: entry.price / months, fromBackend: true }
  }
  return { amount: plan.price_eur_monthly, fromBackend: false }
}
